import React, { useMemo } from 'react';
import Select from 'react-select';
import { ISO3to2Mapping } from '../../../shared/countries.js';
import type { CountryOption } from '../../../shared/types/index.js';
import { getFlagEmoji } from '../../utils/Helpers.js';
import { selectStyles } from './selectStyles.js';

interface Option {
  value: string;
  label: string;
}

interface CountriesFieldProps {
  label: string;
  hint?: string;
  /** Selected ISO3 codes. */
  value: ReadonlyArray<string>;
  options: ReadonlyArray<CountryOption>;
  placeholder?: string;
  disabled?: boolean;
  onChange: (next: Array<string>) => void;
}

function toOption({ iso3, name }: CountryOption): Option {
  const iso2 = ISO3to2Mapping[iso3];
  return { value: iso3, label: iso2 ? `${getFlagEmoji(iso2)} ${name}` : name };
}

/** Multi-select of ISO3 countries, restricted to the options given (the upstream-enabled countries). */
function CountriesField({ label, hint, value, options, placeholder, disabled, onChange }: CountriesFieldProps) {
  const all = useMemo(() => options.map(toOption), [options]);
  const selected = useMemo(
    () => value.map((iso3) => all.find((o) => o.value === iso3) ?? { value: iso3, label: iso3 }),
    [value, all]
  );

  return (
    <div className="sf2">
      <label className="sf2-label">{label}</label>
      <Select<Option, true>
        isMulti
        value={selected}
        options={all}
        placeholder={placeholder}
        isDisabled={disabled}
        closeMenuOnSelect={false}
        onChange={(next) => onChange(next.map((o) => o.value))}
        styles={selectStyles<Option, true>()}
      />
      {hint && <div className="sf2-hint">{hint}</div>}
    </div>
  );
}

export default CountriesField;
